// Animated mockup showing the "Apply via embedded careers widget" flow.
// Renders entirely with HTML + Tailwind + CSS keyframes — no video assets.
//
// Loop sequence:
//   Step 1 (0-3s)   Visitor lands on the employer's careers page (iframe-style
//                   browser chrome) and sees 3 open roles.
//   Step 2 (3-6s)   They click "Apply" on a role — the apply form slides in
//                   with a résumé drop zone.
//   Step 3 (6-9s)   A résumé file animates into the drop zone.
//   Step 4 (9-12s)  Success card slides up: "Application received · AI score 88".
//   Loops every 12s.

import { Briefcase, UploadCloud, CheckCircle2, FileText, Sparkles } from "lucide-react";

export default function CareersShowreel() {
  return (
    <div
      data-testid="careers-showreel"
      className="relative w-full select-none"
    >
      <style>{`
        @keyframes cs-fade-cycle {
          0%, 24% { opacity: 1; transform: translateX(0); }
          25%, 99% { opacity: 0; transform: translateX(-12px); pointer-events: none; }
          100% { opacity: 1; transform: translateX(0); }
        }
        @keyframes cs-fade-cycle-2 {
          0%, 24% { opacity: 0; transform: translateX(12px); pointer-events: none; }
          25%, 49% { opacity: 1; transform: translateX(0); }
          50%, 100% { opacity: 0; transform: translateX(-12px); pointer-events: none; }
        }
        @keyframes cs-fade-cycle-3 {
          0%, 49% { opacity: 0; transform: translateX(12px); pointer-events: none; }
          50%, 74% { opacity: 1; transform: translateX(0); }
          75%, 100% { opacity: 0; transform: translateX(-12px); pointer-events: none; }
        }
        @keyframes cs-fade-cycle-4 {
          0%, 74% { opacity: 0; transform: translateY(16px); pointer-events: none; }
          75%, 99% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(16px); pointer-events: none; }
        }
        @keyframes cs-pulse-ring {
          0% { transform: scale(0.9); opacity: 0.55; }
          70% { transform: scale(1.6); opacity: 0; }
          100% { transform: scale(0.9); opacity: 0; }
        }
        @keyframes cs-drop-in {
          0%, 50% { transform: translateY(-130px) rotate(-8deg); opacity: 0; }
          60% { transform: translateY(0) rotate(0deg); opacity: 1; }
          70% { transform: translateY(0) rotate(0deg); opacity: 1; }
          75%, 100% { transform: translateY(8px) rotate(0deg); opacity: 0; }
        }
        @keyframes cs-progress {
          0%, 49% { width: 0%; }
          55% { width: 25%; }
          62% { width: 70%; }
          70% { width: 100%; }
          74%, 100% { width: 100%; }
        }
        @keyframes cs-cursor {
          0%, 18% { transform: translate(225px, 168px); opacity: 0; }
          22% { transform: translate(225px, 168px); opacity: 1; }
          24% { transform: translate(225px, 168px) scale(0.85); opacity: 1; }
          26%, 100% { transform: translate(225px, 168px) scale(1); opacity: 0; }
        }
        .cs-step-1 { animation: cs-fade-cycle 12s ease-in-out infinite; }
        .cs-step-2 { animation: cs-fade-cycle-2 12s ease-in-out infinite; }
        .cs-step-3 { animation: cs-fade-cycle-3 12s ease-in-out infinite; }
        .cs-step-4 { animation: cs-fade-cycle-4 12s ease-in-out infinite; }
        .cs-cursor { animation: cs-cursor 12s ease-in-out infinite; }
        .cs-resume-drop { animation: cs-drop-in 12s ease-in-out infinite; }
        .cs-progress-bar { animation: cs-progress 12s ease-in-out infinite; }
        .cs-ring::before,
        .cs-ring::after {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: 9999px;
          background: rgba(29, 78, 216, 0.35);
          animation: cs-pulse-ring 2.4s ease-out infinite;
        }
        .cs-ring::after { animation-delay: 1.2s; }
      `}</style>

      {/* Browser chrome */}
      <div className="overflow-hidden border border-slate-200 bg-white shadow-[0_24px_60px_-30px_rgba(15,23,42,0.35)]">
        <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-2">
          <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
          <div className="ml-4 flex flex-1 items-center gap-2 border border-slate-200 bg-white px-3 py-1 text-[11px] text-slate-500">
            <span className="text-slate-400">🔒</span>
            <span className="truncate">acme-careers.com</span>
            <span className="ml-2 inline-flex items-center gap-1 border-l border-slate-200 pl-2 text-[9px] font-bold uppercase tracking-[0.18em] text-blue-700">
              <Sparkles className="h-2.5 w-2.5" /> Hireloop embed
            </span>
          </div>
        </div>

        {/* Stage */}
        <div className="relative h-[420px] bg-slate-50 p-6">
          {/* STEP 1: open roles list */}
          <div className="cs-step-1 absolute inset-6">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-blue-700">
              Careers · Acme
            </div>
            <h3 className="mt-2 font-heading text-2xl font-semibold text-slate-900">
              Open roles at Acme
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              Apply by uploading your résumé below.
            </p>

            <div className="mt-5 space-y-2.5">
              {[
                { title: "Senior Backend Engineer", desc: "Python · AWS · Kubernetes" },
                { title: "Product Designer", desc: "Design systems · Figma" },
                { title: "Growth PM", desc: "Activation · Pricing" },
              ].map((r, i) => (
                <div
                  key={r.title}
                  className={`flex items-center justify-between border border-slate-200 bg-white px-4 py-3 ${
                    i === 0 ? "ring-1 ring-blue-200" : ""
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="border border-slate-200 bg-slate-50 p-1.5">
                      <Briefcase className="h-3.5 w-3.5 text-blue-700" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-slate-900">{r.title}</div>
                      <div className="text-[11px] text-slate-500">{r.desc}</div>
                    </div>
                  </div>
                  <div className="relative">
                    {i === 0 && (
                      <span className="cs-ring absolute -inset-1 rounded-sm" />
                    )}
                    <span
                      className={`relative z-10 inline-flex items-center gap-1 border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] ${
                        i === 0
                          ? "border-blue-700 bg-blue-700 text-white"
                          : "border-slate-300 text-slate-600"
                      }`}
                    >
                      Apply →
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* STEP 2: apply form */}
          <div className="cs-step-2 absolute inset-6">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-blue-700">
              Apply · Acme
            </div>
            <h3 className="mt-2 font-heading text-2xl font-semibold text-slate-900">
              Senior Backend Engineer
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              Python · AWS · Kubernetes — remote-first.
            </p>

            <div className="mt-5 border border-slate-200 bg-white p-4">
              <div className="grid grid-cols-2 gap-2">
                <div className="border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                  Your name
                </div>
                <div className="border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                  Email
                </div>
              </div>
              <div className="mt-3 border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center">
                <UploadCloud className="mx-auto h-6 w-6 text-blue-700" />
                <div className="mt-2 text-xs font-medium text-slate-700">
                  Drop résumé · PDF / DOCX / TXT
                </div>
              </div>
            </div>
          </div>

          {/* STEP 3: uploading with progress */}
          <div className="cs-step-3 absolute inset-6">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-blue-700">
              Uploading
            </div>
            <h3 className="mt-2 font-heading text-2xl font-semibold text-slate-900">
              Scoring résumé…
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              AI is matching the candidate against the role.
            </p>

            <div className="relative mt-5 border border-slate-200 bg-white p-4">
              <div className="cs-resume-drop relative flex items-center gap-3 border border-slate-200 bg-slate-50 px-3 py-2">
                <FileText className="h-4 w-4 text-blue-700" />
                <div className="flex-1 text-[12px] font-medium text-slate-700">
                  resume_jordan_alvarez.pdf
                </div>
                <span className="text-[11px] text-slate-500">218 KB</span>
              </div>
              <div className="mt-4 h-1.5 w-full bg-slate-100">
                <div className="cs-progress-bar h-full bg-blue-700" />
              </div>
              <div className="mt-2 flex justify-between text-[10px] uppercase tracking-[0.16em] text-slate-500">
                <span>Parsing</span>
                <span>AI scoring</span>
                <span>Indexing</span>
              </div>
            </div>
          </div>

          {/* STEP 4: success */}
          <div className="cs-step-4 absolute inset-6">
            <div className="flex h-full flex-col items-center justify-center gap-3 border border-emerald-200 bg-emerald-50 p-6 text-center">
              <CheckCircle2 className="h-10 w-10 text-emerald-700" />
              <div className="font-heading text-xl font-semibold text-emerald-900">
                Application received
              </div>
              <p className="max-w-xs text-xs text-emerald-900">
                Thanks Jordan — your résumé is in front of the Acme team.
              </p>
              <div className="inline-flex items-center gap-2 border border-emerald-300 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700">
                AI score
                <span className="font-heading text-base text-emerald-900">88</span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-600">/ 100</span>
              </div>
              <p className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.22em] text-emerald-700">
                <Sparkles className="h-3 w-3" /> Reviewed with Hireloop PRO
              </p>
            </div>
          </div>

          {/* Animated cursor that "clicks" Apply during step 1 → step 2 transition */}
          <svg
            className="cs-cursor pointer-events-none absolute left-0 top-0 z-20 h-6 w-6 text-slate-900 drop-shadow"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M3 2l7 18 2-7 7-2L3 2z" />
          </svg>
        </div>
      </div>

      {/* Caption */}
      <div className="mt-4 flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 animate-pulse rounded-full bg-blue-700" />
          Live mockup
        </span>
        <span>·</span>
        <span>Embed → Apply → AI score → Pipeline</span>
      </div>
    </div>
  );
}
